import type { ControlInput } from './types';
import type { Vehicle } from './vehicle';
import { DEG, clamp, dot } from './math';

/**
 * Rookie assists applied inside the sim step (so they are deterministic and identical for
 * players and bots): counter-steer help, extra steering smoothing, traction control.
 * ABS lives in the brake code of the vehicle. Real Rally2 cars have neither ABS nor TC; these
 * exist only as Rookie assists.
 */
export function applyAssists(out: ControlInput, raw: ControlInput, v: Vehicle, dt: number): void {
  const a = v.assists;
  out.throttle = raw.throttle;
  out.brake = raw.brake;
  out.handbrake = raw.handbrake;
  out.shiftUp = raw.shiftUp;
  out.shiftDown = raw.shiftDown;

  let steer = raw.steer;
  const fwdSpeed = dot(v.linvel, v.fwd);
  if (a.steeringHelp && fwdSpeed > 4) {
    // Rear-axle slip angle; when the rear slides beyond a dead-band, add counter-steer.
    const b = Math.abs(v.spec.wheels[2].position[2] - v.spec.chassis.centerOfMass[2]);
    const latRear = dot(v.linvel, v.leftDir) - dot(v.angvel, v.up) * b;
    const betaRear = Math.atan2(latRear, fwdSpeed);
    const db = 5 * DEG;
    if (Math.abs(betaRear) > db) {
      const excess = betaRear - Math.sign(betaRear) * db;
      const maxSteer = v.spec.steering.maxAngleDeg * DEG;
      // + angle = left; input + = right.
      steer -= (0.75 * excess) / maxSteer;
    }
  }
  if (a.steeringSmoothing) {
    // Gentle speed-sensitive lock limit for new drivers (full lock stays available slowly).
    const lim = clamp(1 - Math.max(0, fwdSpeed - 12) / 70, 0.45, 1);
    steer = clamp(steer, -lim, lim);
    const k = 1 - Math.exp(-dt / 0.05);
    steer = out.steer + (steer - out.steer) * k;
  }
  out.steer = clamp(steer, -1, 1);

  // Traction control: trim engine torque when the driven wheels spin well past the peak.
  const d = v.drivetrain;
  if (a.tractionControl) {
    let worst = 0;
    for (const w of v.wheels) {
      if (!w.contact) continue;
      const peak = w.surface === 'tarmac' || w.surface === 'concrete' ? 0.1 : 0.2;
      worst = Math.max(worst, w.kappa / peak);
    }
    const target = worst > 2.0 ? clamp(1 - (worst - 2.0) * 0.6, 0.35, 1) : 1;
    const k = target < d.tcFactor ? 1 - Math.exp(-dt / 0.03) : 1 - Math.exp(-dt / 0.25);
    d.tcFactor += (target - d.tcFactor) * k;
  } else d.tcFactor = 1;
}
